import React, { useEffect, useMemo, useState } from "react";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Avatar, DangerCard, FormRow, Input, VbButton } from "@/components/vb";
import ConfirmDialog from "@/components/settings/components/ConfirmDialog";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useWorkspaceRole } from "@/hooks/useWorkspaceRole";
import { useWorkspaceMembers } from "@/hooks/useWorkspaceMembers";
import { deleteWorkspace, renameWorkspace } from "@/services/workspaceService";
import { WorkspaceRole } from "@/types/workspace";

const SettingsWorkspaceGeneral: React.FC = () => {
    const { user } = useAuth();
    const {
        activeWorkspace,
        billingAccount,
        loading: workspaceLoading,
        workspaces,
        refreshWorkspaces,
        setActiveWorkspace: setActiveWorkspaceContext,
    } = useWorkspace();
    const { isWorkspaceOwner, isBillingAccountOwner } = useWorkspaceRole();
    const stripePortalUrl = import.meta.env.VITE_STRIPE_PLAN;
    const canManageSubscription =
        !workspaceLoading && !!billingAccount && isBillingAccountOwner &&
        billingAccount.plan !== "free" && !!stripePortalUrl;
    const {
        members,
        pendingInvitations,
        memberLimit,
        seatsUsed,
        isMemberLimitReached,
        seatPct,
        seatBarClass,
        canReadSeats,
    } = useWorkspaceMembers();

    const [workspaceName, setWorkspaceName] = useState(activeWorkspace?.name ?? "");
    const [savingName, setSavingName] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingWorkspace, setDeletingWorkspace] = useState(false);

    useEffect(() => {
        setWorkspaceName(activeWorkspace?.name ?? "");
    }, [activeWorkspace?.id, activeWorkspace?.name]);

    const isRenameDirty = activeWorkspace ? workspaceName.trim() !== activeWorkspace.name : false;

    const ownerWorkspaces = useMemo(
        () => workspaces.filter((workspace) => workspace.role === WorkspaceRole.Owner),
        [workspaces],
    );
    const isLastOwnedWorkspace = ownerWorkspaces.length <= 1;
    const canDeleteWorkspace = !!activeWorkspace && isWorkspaceOwner && !isLastOwnedWorkspace;

    const owner = useMemo(
        () => members.find((member) => member.role === WorkspaceRole.Owner) ?? null,
        [members],
    );
    const ownerName = owner
        ? `${owner.user.first_name ?? ""} ${owner.user.last_name ?? ""}`.trim() || owner.user.email
        : null;

    const handleSaveWorkspaceName = async (): Promise<void> => {
        if (!activeWorkspace || !isWorkspaceOwner || !workspaceName.trim()) {
            return;
        }
        try {
            setSavingName(true);
            await renameWorkspace(activeWorkspace.id, workspaceName.trim());
            await refreshWorkspaces();
            toast.success("Workspace name updated");
        } catch {
            toast.error("Failed to update workspace name");
            setWorkspaceName(activeWorkspace.name);
        } finally {
            setSavingName(false);
        }
    };

    const handleDeleteWorkspace = async (): Promise<void> => {
        if (!activeWorkspace || !canDeleteWorkspace) {
            return;
        }
        const deletedWorkspaceId = activeWorkspace.id;
        const remainingWorkspaces = workspaces.filter(
            (workspace) => workspace.id !== deletedWorkspaceId,
        );

        try {
            setDeletingWorkspace(true);
            await deleteWorkspace(deletedWorkspaceId);

            const nextWorkspace = remainingWorkspaces[0] ?? null;
            if (nextWorkspace) {
                setActiveWorkspaceContext(nextWorkspace);
            }

            toast.success("Workspace deleted");
            setDeleteDialogOpen(false);
            await refreshWorkspaces();
        } catch {
            toast.error("Failed to delete workspace");
        } finally {
            setDeletingWorkspace(false);
        }
    };

    return (
        <>
            <section className="vb-card">
                <div className="vb-card-hd">
                    <div className="vb-card-hd__left">
                        <h2>Workspace details</h2>
                        <p className="vb-card-hd__sub">
                            This is the billing and team tenant. Everyone here can access all of its
                            companies.
                        </p>
                    </div>
                    {isWorkspaceOwner && (
                        <VbButton
                            type="button"
                            onClick={() => void handleSaveWorkspaceName()}
                            disabled={!isRenameDirty || savingName || !workspaceName.trim()}
                        >
                            {savingName ? "Saving…" : "Save changes"}
                        </VbButton>
                    )}
                </div>

                <FormRow
                    label="Workspace name"
                    hint="Shown in the switcher and on invoices."
                    id="workspace-name-input"
                >
                    {/* Matches Workspace.name (max_length=255). Past it the save
                        fails as a DRF field error, and the catch below reports a
                        generic toast and reverts the box — so the user loses what
                        they typed with no idea why. */}
                    <Input
                        value={workspaceName}
                        onChange={(event) => setWorkspaceName(event.target.value)}
                        disabled={!isWorkspaceOwner || savingName || !activeWorkspace}
                        maxLength={255}
                        placeholder="Workspace name"
                    />
                </FormRow>

                {/* `group` on the read-only rows: they hold no form control, so a
                    <label htmlFor> would point at nothing. */}
                <FormRow
                    label="Workspace avatar"
                    hint="Generated from the workspace name."
                    group
                >
                    <div>
                        <Avatar
                            size="xl"
                            kind="workspace"
                            name={activeWorkspace?.name ?? "?"}
                            ariaLabel={`${activeWorkspace?.name ?? "Workspace"} avatar`}
                        />
                    </div>
                </FormRow>

                <FormRow
                    label="Owner"
                    hint="Read-only. Every workspace has exactly one owner."
                    group
                >
                    <div className="vb-owner-field">
                        {owner ? (
                            <>
                                <Avatar size="sm" round kind="user" name={ownerName ?? ""} decorative />
                                <span className="vb-owner-field__nm">{ownerName}</span>
                                {/* Users without a first/last name fall back to their
                                    email as the display name — don't print it twice. */}
                                {ownerName !== owner.user.email && (
                                    <span className="vb-owner-field__em">{owner.user.email}</span>
                                )}
                            </>
                        ) : (
                            <span className="vb-owner-field__em">Not available</span>
                        )}
                    </div>
                </FormRow>

                {canReadSeats && (
                <FormRow
                    label="Member limit"
                    hint="How many people can be in this workspace. Tied to your plan's seats."
                    group
                >
                    <div className="vb-seat-meter">
                        <div>
                            <div className="vb-seat-meter__v">
                                {seatsUsed} of {memberLimit} seats used
                            </div>
                            <div className="vb-seat-meter__l">
                                {members.length} {members.length === 1 ? "member" : "members"}
                                {pendingInvitations.length > 0
                                    ? ` · ${pendingInvitations.length} ${
                                          pendingInvitations.length === 1 ? "invite" : "invites"
                                      } pending`
                                    : ""}
                            </div>
                        </div>
                        <div
                            className={seatBarClass}
                            role="meter"
                            aria-valuenow={seatsUsed}
                            aria-valuemin={0}
                            aria-valuemax={memberLimit}
                            aria-label="Seats used"
                        >
                            <span style={{ width: `${seatPct}%` }} />
                        </div>
                        {isMemberLimitReached && (
                            <p className="vb-field__help">
                                You're at your seat limit. Remove a member or cancel a pending
                                invite to free one up.
                            </p>
                        )}
                    </div>
                </FormRow>
                )}
            </section>

            {isWorkspaceOwner && (
                <DangerCard
                    title="Danger zone"
                    actionTitle="Delete this workspace"
                    actionDescription={
                        isLastOwnedWorkspace
                            ? "This is your only workspace. Create another before deleting this one."
                            : "Permanently removes the workspace, its companies and their reports."
                    }
                    action={
                        <VbButton
                            type="button"
                            variant="secondary"
                            onClick={() => setDeleteDialogOpen(true)}
                            disabled={!canDeleteWorkspace || deletingWorkspace}
                            title={
                                isLastOwnedWorkspace
                                    ? "You can't delete your last workspace"
                                    : undefined
                            }
                        >
                            {isLastOwnedWorkspace ? "Can't delete last workspace" : "Delete workspace"}
                        </VbButton>
                    }
                    additionalActions={canManageSubscription ? (
                        <div className="vb-danger-card__row">
                            <div className="vb-danger-card__info">
                                <div className="vb-danger-card__info-t">Cancel your subscription</div>
                                <div className="vb-danger-card__info-d">
                                    Cancel your subscription today. You'll be taken to the Stripe
                                    Billing Portal to manage your cancellation. Changes take effect
                                    immediately, you keep access to your workspace until the end of
                                    your current paid period.
                                </div>
                            </div>
                            <VbButton
                                type="button"
                                onClick={() =>
                                    window.open(
                                        `${stripePortalUrl}${
                                            stripePortalUrl.includes("?") ? "&" : "?"
                                        }locked_prefilled_email=${encodeURIComponent(user?.email ?? "")}`,
                                        "_blank",
                                        "noopener,noreferrer",
                                    )
                                }
                            >
                                Manage billing in Stripe
                                <ExternalLink className="h-[13px] w-[13px]" aria-hidden="true" />
                            </VbButton>
                        </div>
                    ) : undefined}
                >
                    These actions can't be undone. Read carefully.
                </DangerCard>
            )}

            <ConfirmDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                title="Delete workspace"
                description={
                    <>
                        This permanently deletes{" "}
                        <strong>{activeWorkspace?.name}</strong>, its companies and their reports.
                        This can't be undone.
                    </>
                }
                confirmLabel="Delete workspace"
                pendingLabel="Deleting…"
                destructive
                pending={deletingWorkspace}
                confirmDisabled={!canDeleteWorkspace}
                onConfirm={() => void handleDeleteWorkspace()}
            />
        </>
    );
};

export default SettingsWorkspaceGeneral;
